import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Copy, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { spinWin, getSpin, setSpin, clearSpin } from "../lib/api";

const SYMBOLS = ["RS", "5", "10", "15", "%", "OFF", "X2", "PRO", "SHIP"];
const ITEM_H = 84;
const SPIN_SEEN = "rsa_spin_seen_v1";

const reelsFor = (prize) =>
  prize.prize_type === "free_bacwater"
    ? ["FREE", "BAC", "x5"]
    : prize.prize_type === "free_tadalafil"
    ? ["FREE", "TAD", "X100"]
    : prize.prize_type === "shipping"
    ? ["FREE", "SHIP", "$0"]
    : [String(prize.percent), "%", "OFF"];

const prizeLabel = (p) =>
  p.prize_type === "free_bacwater"
    ? "Free BAC Water 5-pack with your order"
    : p.prize_type === "free_tadalafil"
    ? "Free Tadalafil x100 with your order"
    : p.prize_type === "shipping"
    ? "Free shipping"
    : `${p.percent}% off`;

const Reel = ({ spinning, spinId, final, index, landed }) => {
  const strip = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 26; i++) arr.push(SYMBOLS[(i * 7 + index * 3 + spinId) % SYMBOLS.length]);
    arr.push(final);
    return arr;
  }, [spinId, final, index]);

  return (
    <div
      className={`w-20 sm:w-24 overflow-hidden border bg-ink-900 transition-colors ${
        landed ? "border-accent" : "border-[#27272A]"
      }`}
      style={{ height: ITEM_H }}
      data-testid={`spin-reel-${index}`}
    >
      <motion.div
        key={spinId}
        initial={{ y: 0 }}
        animate={spinning || landed ? { y: -ITEM_H * (strip.length - 1) } : { y: 0 }}
        transition={
          spinning
            ? { duration: 1.5 + index * 0.45, ease: [0.12, 0.8, 0.18, 1] }
            : { duration: 0.3 }
        }
      >
        {strip.map((s, i) => (
          <div
            key={i}
            className={`flex items-center justify-center font-display font-black ${
              s.length > 3 ? "text-xl" : "text-3xl"
            } ${landed && i === strip.length - 1 ? "text-accent" : "text-white"}`}
            style={{ height: ITEM_H }}
          >
            {s}
          </div>
        ))}
      </motion.div>
    </div>
  );
};

const fmt = (secs) => `${String(Math.floor(secs / 60)).padStart(2, "0")}:${String(secs % 60).padStart(2, "0")}`;

export const SpinWin = () => {
  const [open, setOpen] = useState(false);
  const [awaiting, setAwaiting] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [spinId, setSpinId] = useState(0);
  const [finals, setFinals] = useState(["?", "?", "?"]);
  const [result, setResult] = useState(null);
  const [claimed, setClaimed] = useState(getSpin());
  const [now, setNow] = useState(Date.now());
  const [copied, setCopied] = useState(false);
  const pending = useRef(null);

  useEffect(() => {
    const maybeOpen = () => {
      if (sessionStorage.getItem(SPIN_SEEN)) return;
      if (localStorage.getItem("rsa_compliance_ack_v1") !== "true") return;
      sessionStorage.setItem(SPIN_SEEN, "1");
      if (getSpin()) return;
      setTimeout(() => setOpen(true), 900);
    };
    maybeOpen();
    const onCleared = () => setClaimed(null);
    window.addEventListener("rsa-gate-accepted", maybeOpen);
    window.addEventListener("rsa-spin-cleared", onCleared);
    return () => {
      window.removeEventListener("rsa-gate-accepted", maybeOpen);
      window.removeEventListener("rsa-spin-cleared", onCleared);
    };
  }, []);

  useEffect(() => {
    if (!result && !claimed) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [result, claimed]);

  useEffect(() => {
    if (claimed && new Date(claimed.expires_at).getTime() <= now) {
      clearSpin();
      setClaimed(null);
    }
  }, [now, claimed]);

  const spin = async () => {
    if (awaiting || spinning || result) return;
    setAwaiting(true);
    try {
      pending.current = await spinWin();
    } catch {
      pending.current = null;
    }
    setAwaiting(false);
    if (!pending.current) {
      toast.error("Spin failed — give it another go.");
      return;
    }
    setFinals(reelsFor(pending.current));
    setSpinId((n) => n + 1);
    setSpinning(true);
    setTimeout(() => {
      setSpinning(false);
      setResult(pending.current);
    }, 2600);
  };

  const claim = () => {
    setSpin(result);
    setClaimed(result);
    setOpen(false);
    toast.success(`Code ${result.code} locked in — ${prizeLabel(result)} for 10 minutes.`);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(result.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const secsLeft = result
    ? Math.max(0, Math.floor((new Date(result.expires_at).getTime() - now) / 1000))
    : 0;
  const claimedSecs = claimed
    ? Math.max(0, Math.floor((new Date(claimed.expires_at).getTime() - now) / 1000))
    : 0;

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-ink-900/90 backdrop-blur-md flex items-center justify-center px-5"
            data-testid="spin-overlay"
          >
            <motion.div
              initial={{ y: 30, opacity: 0, scale: 0.96 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-md w-full bg-ink-800 border border-[#27272A] clip-corner-lg p-7 sm:p-8 relative"
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
                data-testid="spin-dismiss"
                aria-label="Close"
              >
                <X size={18} />
              </button>

              <div className="flex items-center gap-2 text-accent mb-3">
                <Zap size={16} />
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em]">
                  Welcome bonus
                </span>
              </div>
              <h2 className="font-display font-black tracking-tighter text-4xl leading-none">
                SPIN TO WIN
              </h2>
              <p className="text-zinc-400 text-sm mt-3 leading-relaxed">
        Every spin wins — a <strong className="text-white">free Tadalafil</strong> or{" "}
        <strong className="text-white">free BAC Water</strong> added to your order, automatically. Your prize
                only lives for <strong className="text-accent">10 minutes</strong>.
              </p>

              <div className="flex justify-center gap-2.5 mt-7">
                {finals.map((f, i) => (
                  <Reel
                    key={i}
                    index={i}
                    final={f}
                    spinning={spinning}
                    spinId={spinId}
                    landed={!!result}
                  />
                ))}
              </div>

              {!result ? (
                <button
                  onClick={spin}
                  disabled={awaiting || spinning}
                  className="w-full mt-7 bg-accent hover:bg-accent-hover disabled:opacity-60 text-white font-display font-bold uppercase tracking-wide text-lg py-4 flex items-center justify-center gap-2 transition-colors clip-corner"
                  data-testid="spin-button"
                >
                  {(awaiting || spinning) && <Loader2 size={17} className="animate-spin" />}
                  {awaiting || spinning ? "Spinning…" : "Spin Now"}
                </button>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-7"
                  data-testid="spin-result"
                >
                  <p className="text-center font-display font-black text-3xl tracking-tighter text-accent" data-testid="spin-prize">
                    {result.prize_type === "shipping"
                      ? "FREE SHIPPING UNLOCKED"
                      : result.prize_type === "free_tadalafil"
                      ? "FREE TADALAFIL UNLOCKED"
                      : result.prize_type === "free_bacwater"
                      ? "FREE BAC WATER UNLOCKED"
                      : `${result.percent}% OFF UNLOCKED`}
                  </p>
                  <div className="mt-4 flex items-center justify-between border border-[#27272A] bg-ink-900 px-4 py-3">
                    <span className="font-mono-tech text-lg text-white" data-testid="spin-code">
                      {result.code}
                    </span>
                    <button
                      onClick={copyCode}
                      className={`flex items-center gap-1.5 border px-3 py-1.5 font-mono-tech text-[10px] uppercase tracking-widest transition-colors ${
                        copied
                          ? "border-emerald-400 text-emerald-400"
                          : "border-[#27272A] text-zinc-300 hover:border-accent hover:text-accent"
                      }`}
                      data-testid="spin-copy"
                    >
                      {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                  <p
                    className="text-center font-mono-tech text-xs uppercase tracking-widest text-zinc-400 mt-3"
                    data-testid="spin-timer"
                  >
                    Expires in <span className="text-accent font-bold">{fmt(secsLeft)}</span>
                  </p>
                  <button
                    onClick={claim}
                    className="w-full mt-4 bg-accent hover:bg-accent-hover text-white font-display font-bold uppercase tracking-wide text-lg py-4 transition-colors clip-corner"
                    data-testid="spin-claim"
                  >
                    Claim &amp; Start Shopping
                  </button>
                </motion.div>
              )}

              {!result && (
                <p className="text-center font-mono-tech text-[10px] uppercase tracking-widest text-zinc-600 mt-4">
                  Applied automatically at checkout
                </p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {claimed && !open && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            className="fixed bottom-4 left-4 z-[80] flex items-center gap-3 bg-ink-800 border border-accent/50 px-4 py-2.5 clip-corner"
            data-testid="spin-pill"
          >
            <Zap size={14} className="text-accent" />
            <span className="font-mono-tech text-[11px] uppercase tracking-widest text-zinc-300">
              {claimed.code} · {prizeLabel(claimed)}
            </span>
            <span className="font-mono-tech text-[11px] font-bold text-accent" data-testid="spin-pill-timer">
              {fmt(claimedSecs)}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
